<template>
  <v-menu
    v-model="menuOpen"
    top
    offset-y
    nudge-top="8"
    min-width="320"
    max-width="360"
    :close-on-content-click="false"
    content-class="gw-options__menu"
  >
    <template v-slot:activator="{ on, attrs }">
      <span class="gw-options__activator" v-bind="attrs" v-on="on">
        <rmr-action-button
          label="Options"
          :icon="icons.mdiTune"
          variant="translucent"
          size="xs"
        />
        <!-- Signals a non-default setting without forcing the menu open. -->
        <span v-if="hasCustomOptions" class="gw-options__dot" />
      </span>
    </template>

    <div class="gw-options__panel">
      <div class="gw-options__block gw-options__block--legacy">
        <label class="rmr-table-shell__filter-label">Template de prompt</label>
        <custom-prompt-template-filter
          :no-url="true"
          :force-site-id="siteId"
          preselect-default
          store="aiFactory"
        />
      </div>
      <div class="gw-options__block gw-options__block--legacy">
        <label class="rmr-table-shell__filter-label">Image</label>
        <aif-image-mode-toggle v-model="imageHandler" layout="select" />
      </div>
      <!-- Blocs propres a la page hote (statut de publication, options de
           titre...). Le panneau est concu pour en empiler plusieurs. -->
      <slot />
    </div>
  </v-menu>
</template>

<script>
import { computed, ref } from "@vue/composition-api";
import { mdiTune } from "@mdi/js";

import AifImageModeToggle from "@/components/ai-factory/common/AifImageModeToggle.vue";
import CustomPromptTemplateFilter from "@/components/ai-factory/full-auto/filters/CustomPromptTemplateFilter.vue";
import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";

import useProxy from "@/hooks/useProxy";

export default {
  name: "GenerationOptionsMenu",
  components: {
    AifImageModeToggle,
    CustomPromptTemplateFilter,
    RmrActionButton,
  },
  props: {
    siteId: {
      type: Number,
      default: 0,
    },
    /** La page hote signale qu'un de SES blocs porte un reglage non-defaut. */
    hasExtraOptions: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { store } = useProxy();

    const imageHandler = computed({
      get: () => store.getters["aiFactory/getGenerationImageHandler"],
      set: (value) =>
        store.dispatch("aiFactory/updateGenerationImageHandler", value),
    });

    const hasCustomOptions = computed(() => {
      const templateId = Number(
        store.getters["aiFactory/getCustomPromptTemplate"] || 0
      );
      return (
        templateId > 0 ||
        imageHandler.value !== "generate" ||
        props.hasExtraOptions
      );
    });

    return {
      hasCustomOptions,
      icons: { mdiTune },
      imageHandler,
      menuOpen: ref(false),
    };
  },
};
</script>

<style lang="scss" scoped>
// Positioning context for the dot below.
.gw-options__activator {
  position: relative;
  display: inline-flex;
}

.gw-options__dot {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 8px;
  height: 8px;
  border-radius: $rmr-radius-pill;
  background: $rmr-indigo;
  border: 1.5px solid $rmr-text-primary;
}
</style>

<style lang="scss">
// Unscoped: v-menu content is teleported out of this component's subtree.
.gw-options__menu {
  border-radius: $rmr-radius-xl !important;
  box-shadow: 0 8px 32px rgba(15, 23, 42, 0.18) !important;
}

.gw-options__panel {
  background: $rmr-surface-0;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

// CustomPromptTemplateFilter is shared (9 call sites): realign it here rather
// than change it.
.gw-options__block--legacy {
  .v-input,
  .v-text-field {
    margin-top: 0 !important;
    padding-top: 0 !important;
  }

  .v-input__slot {
    min-height: 38px !important;
    border-radius: $rmr-radius-md !important;
  }

  .v-input__slot fieldset {
    border-color: $rmr-border-hover !important;
  }

  .v-text-field__details {
    display: none;
  }

  // The block already has its own label above the field.
  .v-label {
    display: none;
  }
}
</style>
